/* eslint-disable */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { FullScreenLoading, Tab, Tabs, HeaderTabs } from 'pg-ui-library';

import { IPage } from 'src/interfaces';
import { ReactComponent as PersonalSVG } from 'src/assets/personal.svg';
import { ReactComponent as EmploymentSVG } from 'src/assets/employment.svg';
import { ReactComponent as PayHistorySvg } from 'src/assets/payHistory.svg';
import { ReactComponent as InformationSvg } from 'src/assets/information.svg';
import { ReactComponent as LoginSvg } from 'src/assets/login.svg';
import { ReactComponent as NotesSvg } from 'src/assets/notes.svg';
import { ReactComponent as BenefitsSvg } from 'src/assets/benefits_icn.svg';

import { useTypedSelector } from 'src/hooks/useTypedSelector';
import { Modify } from 'src/utils/modifyType';
import { IEmployee } from 'src/interfaces/employee';
import * as Bs from 'react-icons/bs';

import { RolePermission } from 'src/components/sidebar';
import useRolePermission from 'src/hooks/useRolePermission';
import { Container } from './styles';
import PersonalInformationTab from './components/personal-information-tab';
import PersonalDependentsTab from './components/personal-dependents-tab';
import PersonalContactsTab from './components/personal-contacts-tab';
import EmploymentNotesTab from './components/employment-notes-tab';
import EmploymentLoginTab from './components/employment-login-tab';
import EmploymentInformationTab from './components/employment-information-tab';
import { EmployeePayHistoryTab } from './components/employee-pay-history-tab';
import EmployeeBenefitsTab from './components/employee-benefits-tab';
import EmployeeRules from './components/employee-rules';
import TaxFormsTab from './components/tax-forms-tab';

export interface IEmployeeForm
  extends Modify<
    IEmployee,
    {
      hireDate: Date | string;
      rehireDate: Date | string;
      terminationDate: Date | string;
      birthDate: Date | string;
    }
  > {}

const Employee: React.FC<IPage> = () => {
  const { employee: selectedEmployee } = useTypedSelector(state => state.employees.data.selectedEmployee);

  const { activeTab } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  const onTabChange = (tab: string) => {
    if (activeTab !== tab) {
      navigate(`/employee/${tab}`);
    }
  };

  useEffect(() => {
    if (!selectedEmployee) {
      navigate('/employee-list', { replace: true });
    } else {
      setLoading(false);
    }
  }, [selectedEmployee]);

  const { t } = useTranslation();

  const { hasRolePermission } = useRolePermission();

  const headerTabKeys = [
    { key: 'personal', icon: <PersonalSVG />, label: 'Personal' },
    { key: 'employment', icon: <EmploymentSVG />, label: 'Employment' },
    { key: 'pay-history', icon: <PayHistorySvg />, label: 'Pay History' },
  ];

  const subTabKeys = useMemo(() => {
    const tabs = [
      {
        key: 'information',
        icon: <InformationSvg />,
        label: 'Information',
      },
      { key: 'login', icon: <LoginSvg />, label: 'Login' },
      { key: 'notes', icon: <NotesSvg />, label: 'Notes' },
    ];
    if (hasRolePermission(RolePermission.SUPER_ADMIN)) {
      tabs.push(
        { key: 'benefits', icon: <BenefitsSvg />, label: 'Benefits' },
        {
          key: 'rules',
          icon: <Bs.BsPersonGear style={{ width: '20px', height: '20px' }} />,
          label: 'Rules',
        }
      );
    }
    return tabs;
  }, []);

  return (
    <Container>
      <HeaderTabs
        title={t('employeePage.title')}
        subtitle={selectedEmployee?.name || ''}
        backTo="/employee-list"
        defaultTab={activeTab}
        tabsKeys={headerTabKeys}
        onTabChange={(tab: string) => onTabChange(tab)}
      >
        <Tab key="employment">
          <div>
            <Tabs defaultTab="information" tabsKeys={subTabKeys} onTabChange={() => {}}>
              <Tab key="information">
                <EmploymentInformationTab />
              </Tab>
              <Tab key="login">
                <EmploymentLoginTab />
              </Tab>
              <Tab key="notes">
                <EmploymentNotesTab />
              </Tab>
              <Tab key="benefits">
                <EmployeeBenefitsTab />
              </Tab>
              <Tab key="rules">
                <EmployeeRules />
              </Tab>
            </Tabs>
          </div>
        </Tab>
        <Tab key="personal">
          <div>
            <Tabs
              defaultTab="information"
              tabsKeys={[
                {
                  key: 'information',
                  icon: <InformationSvg />,
                  label: 'Information',
                },
                { key: 'dependents', icon: <LoginSvg />, label: 'Dependents' },
                { key: 'contacts', icon: <NotesSvg />, label: 'Contacts' },
                { key: 'tax-forms', icon: <NotesSvg />, label: 'Tax-Forms' },
              ]}
              onTabChange={() => {}}
            >
              <Tab key="information">
                <PersonalInformationTab />
              </Tab>
              <Tab key="dependents">
                <PersonalDependentsTab />
              </Tab>
              <Tab key="contacts">
                <PersonalContactsTab />
              </Tab>
              <Tab key="tax-forms">
                <TaxFormsTab />
              </Tab>
            </Tabs>
          </div>
        </Tab>
        <Tab key="pay-history">
          <EmployeePayHistoryTab />
        </Tab>
      </HeaderTabs>
    </Container>
  );
};
export default Employee;
